import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Eye, Trash2, Image as ImageIcon, Plus, Minus, Pencil, Check } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useState, useEffect } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface GalleryImage {
  id: string;
  file_name: string;
  file_path: string;
  file_type: string;
  source: "upload" | "camera";
  ai_summary?: string | null;
  created_at: string;
}

interface GalleryImageRowProps {
  image: GalleryImage;
  isOpen: boolean;
  onToggle: () => void;
  onDelete?: () => void;
  onUpdate?: () => void;
  isEditingDate?: boolean;
  onStartEditDate?: () => void;
  onCancelEditDate?: () => void;
  onSaveDate?: () => void;
}

export function GalleryImageRow({
  image,
  isOpen,
  onToggle,
  onDelete,
  onUpdate,
  isEditingDate = false,
  onStartEditDate,
  onCancelEditDate,
  onSaveDate
}: GalleryImageRowProps) {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [editedDate, setEditedDate] = useState("");
  const [isSavingDate, setIsSavingDate] = useState(false);

  useEffect(() => {
    loadImageUrl();
  }, [image.file_path]);

  async function loadImageUrl() {
    try {
      const { data, error } = await supabase.storage
        .from("patient-docs")
        .createSignedUrl(image.file_path, 3600);

      if (error) throw error;
      if (data?.signedUrl) {
        setImageUrl(data.signedUrl);
      }
    } catch (error) {
      console.error("Error loading image URL:", error);
    } finally {
      setLoading(false);
    }
  }

  const formattedDate = format(new Date(image.created_at), "dd/MM/yyyy", {
    locale: ptBR,
  });

  // Inicializar editedDate quando começar a editar
  useEffect(() => {
    if (isEditingDate) {
      // Converter data para formato YYYY-MM-DD usando timezone local
      const dateObj = new Date(image.created_at);
      const year = dateObj.getFullYear();
      const month = String(dateObj.getMonth() + 1).padStart(2, '0');
      const day = String(dateObj.getDate()).padStart(2, '0');
      setEditedDate(`${year}-${month}-${day}`);
    } else {
      setEditedDate("");
    }
  }, [isEditingDate, image.created_at]);

  const handleStartEditDate = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    onStartEditDate?.();
  };

  const handleCancelEditDate = () => {
    setEditedDate("");
    onCancelEditDate?.();
  };

  const handleSaveDate = async () => {
    if (!editedDate) return;

    try {
      setIsSavingDate(true);
      // Converter data do formato YYYY-MM-DD para ISO string usando timezone local
      const [year, month, day] = editedDate.split('-').map(Number);
      // Criar data no timezone local (não UTC)
      const dateObj = new Date(year, month - 1, day, 12, 0, 0, 0);

      const { error } = await supabase
        .from("patient_gallery")
        .update({ created_at: dateObj.toISOString() })
        .eq("id", image.id);

      if (error) throw error;

      toast.success("Data atualizada com sucesso!");
      setEditedDate("");
      onSaveDate?.();
    } catch (error) {
      console.error("Erro ao salvar data:", error);
      toast.error("Erro ao salvar data");
    } finally {
      setIsSavingDate(false);
    }
  };

  const handleViewImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (imageUrl) {
      window.open(imageUrl, '_blank');
    }
  };

  const handleDelete = async () => {
    try {
      // Deletar do storage
      const { error: storageError } = await supabase.storage
        .from("patient-docs")
        .remove([image.file_path]);

      if (storageError) throw storageError;

      // Deletar do banco
      const { error: dbError } = await supabase
        .from("patient_gallery")
        .delete()
        .eq("id", image.id);

      if (dbError) throw dbError;

      toast.success("Imagem excluída com sucesso!");
      onDelete?.();
    } catch (error) {
      console.error("Erro ao excluir:", error);
      toast.error("Erro ao excluir imagem");
    }
  };

  return (
    <div className="w-full bg-background border-b">
      <div className="w-full">
        {/* Cabeçalho - Layout em Grid */}
        <div
          className="grid grid-cols-12 gap-2 px-4 py-3 border-b items-center cursor-pointer min-w-[800px] overflow-visible"
          onClick={onToggle}
        >
          <div className="col-span-1">
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onToggle();
              }}
              className="flex items-center justify-center w-6 h-6 rounded-full p-0"
            >
              {isOpen ? <Minus className="h-3 w-3" /> : <Plus className="h-3 w-3" />}
            </Button>
          </div>

          <div 
            className="col-span-4 md:col-span-2 text-sm font-medium flex items-center gap-1 min-w-0"
            onClick={(e) => e.stopPropagation()}
          >
            {isEditingDate ? (
              <div className="flex items-center gap-1.5 min-w-0 flex-wrap">
                <Input
                  type="date"
                  value={editedDate}
                  onChange={(e) => setEditedDate(e.target.value)}
                  className="h-7 text-xs w-[110px] flex-shrink-0"
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSaveDate();
                    } else if (e.key === 'Escape') {
                      handleCancelEditDate();
                    }
                  }}
                  autoFocus
                />
                <Button
                  size="sm"
                  variant="default"
                  className="h-7 px-2 text-xs whitespace-nowrap flex-shrink-0"
                  onClick={handleSaveDate}
                  disabled={isSavingDate}
                >
                  <Check className="h-3 w-3 mr-1" />
                  Salvar
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 px-2 text-xs whitespace-nowrap flex-shrink-0"
                  onClick={handleCancelEditDate}
                  disabled={isSavingDate}
                >
                  Cancelar
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-1 min-w-0 w-full">
                <span className="truncate">{formattedDate}</span>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-5 w-5 p-0 flex-shrink-0 hover:bg-muted ml-0.5"
                  onClick={handleStartEditDate}
                  title="Editar data"
                >
                  <Pencil className="h-3 w-3 text-muted-foreground" />
                </Button>
              </div>
            )}
          </div>

          <div className="col-span-7 md:col-span-2 flex items-center gap-2 overflow-hidden">
            <span className="text-base flex-shrink-0">🖼️</span>
            <h3 className="text-sm font-semibold truncate" title="Imagem">
              Imagem
            </h3>
          </div>

          <div className="hidden md:block md:col-span-3 text-sm text-muted-foreground truncate" title={image.file_name}>
            {image.file_name}
          </div>

          {/* Botões de Ação - Apenas Desktop */}
          <div className="hidden md:flex md:col-span-4 items-center justify-start gap-1.5 flex-wrap" onClick={(e) => e.stopPropagation()}>
            <Button
              variant="outline"
              size="sm"
              className="flex items-center gap-1 px-2.5 py-1.5 h-auto text-xs whitespace-nowrap"
              onClick={handleViewImage}
              disabled={!imageUrl}
            >
              <Eye className="h-3.5 w-3.5" />
              <span className="text-xs">Ver</span>
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="flex items-center gap-1 px-2.5 py-1.5 h-auto text-xs whitespace-nowrap text-destructive border-destructive hover:bg-destructive/10 hover:text-destructive"
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  <span className="text-xs">Excluir</span>
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle>
                  <AlertDialogDescription>
                    Tem certeza que deseja excluir esta imagem? Esta ação não pode ser desfeita.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                  <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                    Sim, Excluir
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>

        {/* Botões de Ação - Mobile/Tablet */}
        {isOpen && (
          <div className="md:hidden px-4 py-2 border-b" onClick={(e) => e.stopPropagation()}>
            <span className="text-xs text-muted-foreground uppercase tracking-wider mb-2 block">AÇÕES</span>
            <div className="grid grid-cols-2 gap-2">
              <Button
                variant="outline"
                size="sm"
                className="flex items-center justify-center gap-1.5 px-3 py-1.5 h-auto w-full"
                onClick={handleViewImage}
                disabled={!imageUrl}
              >
                <Eye className="h-4 w-4 text-primary" />
                <span className="text-xs">Ver</span>
              </Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex items-center justify-center gap-1.5 px-3 py-1.5 h-auto w-full text-destructive border-destructive hover:bg-destructive/10"
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                  >
                    <Trash2 className="h-4 w-4" />
                    <span className="text-xs">Excluir</span>
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle>
                    <AlertDialogDescription>
                      Tem certeza que deseja excluir esta imagem? Esta ação não pode ser desfeita.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                    <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
                      Excluir
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

